import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Plus, Loader2 } from "lucide-react";
import { useAddresses, useCreateAddress, useUpdateAddress, useDeleteAddress, useSetDefaultAddress } from "@/hooks/use-addresses";
import { AddressCard } from "@/components/AddressCard";
import { AddressForm } from "@/components/AddressForm";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Address, CreateAddressDto } from "@/lib/types";
import { cn } from "@/lib/utils";

export default function Addresses() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const { toast } = useToast();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [deletingAddress, setDeletingAddress] = useState<Address | null>(null);

  const { data: addresses, isLoading } = useAddresses();
  const createMutation = useCreateAddress();
  const updateMutation = useUpdateAddress();
  const deleteMutation = useDeleteAddress();
  const setDefaultMutation = useSetDefaultAddress();

  const handleAddNew = () => {
    setEditingAddress(null);
    setIsFormOpen(true);
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);
    setIsFormOpen(true);
  };

  const handleDelete = (address: Address) => {
    setDeletingAddress(address);
  };

  const handleSetDefault = async (address: Address) => {
    try {
      await setDefaultMutation.mutateAsync(address.id);
      toast({
        title: t("addresses.defaultUpdated"),
        description: t("addresses.defaultUpdatedDesc"),
      });
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleFormSubmit = async (data: CreateAddressDto) => {
    try {
      if (editingAddress) {
        await updateMutation.mutateAsync({ id: editingAddress.id, data });
        toast({
          title: t("addresses.updated"),
          description: t("addresses.updatedDesc"),
        });
      } else {
        await createMutation.mutateAsync(data);
        toast({
          title: t("addresses.created"),
          description: t("addresses.createdDesc"),
        });
      }
      setIsFormOpen(false);
      setEditingAddress(null);
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingAddress) return;
    try {
      await deleteMutation.mutateAsync(deletingAddress.id);
      toast({
        title: t("addresses.deleted"),
        description: t("addresses.deletedDesc"),
      });
      setDeletingAddress(null);
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const maxAddresses = 10;
  const canAddMore = (addresses?.length || 0) < maxAddresses;

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MapPin className="w-6 h-6 text-primary" />
              {t("addresses.title")}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">{t("addresses.subtitle")}</p>
          </div>
          {canAddMore && (
            <Button onClick={handleAddNew} data-testid="button-add-address">
              <Plus className="w-4 h-4 me-2" />
              {t("addresses.addNew")}
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : addresses?.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">{t("addresses.noAddresses")}</h3>
            <p className="text-muted-foreground text-sm mb-6">{t("addresses.noAddressesDesc")}</p>
            <Button onClick={handleAddNew} data-testid="button-add-first-address">
              <Plus className="w-4 h-4 me-2" />
              {t("addresses.addFirst")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {addresses?.map((address) => (
              <AddressCard
                key={address.id}
                address={address}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
              />
            ))}
            {!canAddMore && (
              <p className="text-center text-sm text-muted-foreground py-4">
                {t("addresses.maxReached", { max: maxAddresses })}
              </p>
            )}
          </div>
        )}
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir={isRtl ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle>
              {editingAddress ? t("addresses.editAddress") : t("addresses.addAddress")}
            </DialogTitle>
            <DialogDescription>
              {editingAddress ? t("addresses.editAddressDesc") : t("addresses.addAddressDesc")}
            </DialogDescription>
          </DialogHeader>
          <AddressForm
            address={editingAddress}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingAddress(null);
            }}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingAddress} onOpenChange={(open) => !open && setDeletingAddress(null)}>
        <AlertDialogContent dir={isRtl ? "rtl" : "ltr"}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("addresses.confirmDelete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("addresses.confirmDeleteDesc", { label: deletingAddress?.label })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={cn("gap-2", isRtl && "flex-row-reverse")}>
            <AlertDialogCancel data-testid="button-cancel-delete">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("addresses.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
